import parseRenderingContext from '@utils/parse-rendering-context';
import addressBookStore from '@stores/address-book/address-book-store';
import AddressBookForm from './address-book-form';
import AddressBookLine from './address-book-line';
import UpsCopyright from '../ups-copyright/ups-copyright';
import template, { lineItem, lineList } from './templates/address-book';
import { context as defaultContext } from './address-book-defaults';
import { DEFAULT_OPTIONS as formDefaults } from './address-book-form';

export default class AddressBook {
    constructor($el, context) {
        context = {
            ...defaultContext,
            ...context
        };

        const $parsed = $($.parseHTML(template));
        $el.append($parsed);

        this.$el = $el;

        this.$heading = this.$el.find('[data-region="heading"]');
        this.$loader = this.$el.find('[data-region="loader"]');
        this.$lines =this.$el.find('[data-region="lines"]');
        this.$formSection =this.$el.find('[data-region="formSection"]');
        this.$form = this.$el.find('[data-region="form"]');
        this.$ups = this.$el.find('[data-region="ups"]');
        this.$noAddresssMessage = $('.js-address-book').find('.js-add-address');

        this.$addCtrl = this.$el.find('[data-control="add"]');

        this.form = null;
        this.formTriggerElement = null;

        this.lines = [];

        this.context = context;
    }

    init() {
        this.$loader.hide();

        this.setupLines();
        this.setupListeners();
        this.renderLines();
        this.renderUps();

        this.showLines();
    }

    onAddressesUpdate() {
        this.setupLines();
        this.renderLines();

        if(addressBookStore.getters.wasChanged) {
            this.handleFocus();
        }
    }

    onAfterSave(addressId) {
        this.showLines();

        if(addressId) { // edited address
            // lines will be re-rendered so original trigger element is lost
            this.formTriggerElement = null;
            this.handleFocus();
        } else { // new address
            this.formTriggerElement?.focus();
        }
    }

    onCancel() {
        this.showLines();
        this.form.destroy();
        this.form = null;
        if(this.lines.length == 0){
            this.$noAddresssMessage.show();
        }
    }

    onEdit(addressId) {
        this.$noAddresssMessage.hide();
        this.setupForm(addressId);
        this.showForm();
    }

    focus() {
        if(this.$heading.length) {
            this.$heading
                .attr('tabindex', 0)
                .one('blur', () => this.$heading.removeAttr('tabindex'))
                .get(0)
                .focus();
        }
    }

    handleFocus() {
        const newIndex = addressBookStore.getters.addressIds.indexOf(addressBookStore.state.lastChanged.addressId);
        if(newIndex > -1) {
            this.lines[newIndex].focus();
        } else {
            const fallbackIndex = addressBookStore.state.lastChanged.index - 1;
            if(fallbackIndex > -1 && this.lines && this.lines[fallbackIndex]) {
                this.lines[fallbackIndex].focus();
            } else {
                this.focus();
            }
        }
    }

    renderLines() {
        this.$lines.empty();
        this.$noAddresssMessage.hide();

        if(this.lines.length) {
            const fragment = document.createDocumentFragment();
            const [ list ] = $.parseHTML(lineList());

            this.lines.forEach((line) => {
                const addressLine = line.render()?.get(0);

                if(addressLine) {
                    const [ li ] = $.parseHTML(lineItem());
                    li.appendChild(addressLine);
                    list.appendChild(li);
                }
            });

            fragment.appendChild(list);

            this.$lines.html(fragment);
        }else{
            this.$noAddresssMessage.show();
        }
    }

    renderUps() {
        const ups = new UpsCopyright(this.$ups, { replaceOriginEl: true }, {
            logo: {
                alt: this.context.UpsLogo.Alt,
                url: this.context.UpsLogo.Url
            },
            text: this.context.UpsCopyright
        });

        ups.render();
    }

    setupForm(addressId) {
        const schema = this.setupFormSchema(addressId);

        const options = {
            addressId,
            onAfterSave: () => this.onAfterSave(addressId),
            onCancel: () => this.onCancel(),
            schema
        };

        const context = {
            cancel: this.context.Cancel,
            heading: addressId ? this.context.EditShippingAddress : this.context.AddShippingAddress,
            loadingIcon: this.context.LoadingIcon,
            requiredToken: this.context.RequiredToken,
            save: this.context.SaveAddress,
            loadingSrText: this.context.LoadingSrText,
            validation: this.context.RequiredMessage
        };

        this.form = new AddressBookForm(this.$form, options, context);
        this.form.render();
    }

    setupFormSchema(addressId) {
        const addressInfo = addressBookStore.getters.addressInfo(addressId);
        const defaultSchema = { ...formDefaults.schema };
        return {
            primaryAddress: {
                ...defaultSchema.primaryAddress,
                label: this.context.SetPrimaryCheckbox,
                name: 'IsPrimary',
                value: addressInfo.isPrimary
            },
            firstName: {
                ...defaultSchema.firstName,
                label: this.context.FirstName,
                name: 'FirstName',
                value: addressInfo.firstName || ''
            },
            lastName: {
                ...defaultSchema.lastName,
                label: this.context.LastName,
                name: 'LastName',
                value: addressInfo.lastName || ''
            },
            address: {
                ...defaultSchema.address,
                label: this.context.Address,
                name: 'Address1',
                value: addressInfo.address1 || ''
            },
            address2: {
                ...defaultSchema.address2,
                label: this.context.Address2,
                name: 'Address2',
                value: addressInfo.address2 || ''
            },
            city: {
                ...defaultSchema.city,
                label: this.context.City,
                name: 'City',
                value: addressInfo.city || ''
            },
            state: {
                ...defaultSchema.state,
                label: this.context.State,
                name: 'StateCode',
                value: addressInfo.stateCode || ''
            },
            zip: {
                ...defaultSchema.zip,
                label: this.context.Zip,
                name: 'ZipPostalCode',
                value: addressInfo.zip || ''
            },
            phone: {
                ...defaultSchema.phone,
                label: this.context.Phone,
                name: 'PhoneNumber',
                value: addressInfo.phone || ''
            }
        };
    }

    setupLines() {
        const lines = [];

        addressBookStore.getters.addressIds.forEach((addressId) => {
            lines.push(new AddressBookLine(null, this.context, addressId, {
                onEdit: (...args) => this.onEdit(...args)
            }));
        });

        this.lines.forEach((line) => line.destroy());
        this.lines = lines;
    }

    setupListeners() {
        addressBookStore.watch('addresses', () => this.onAddressesUpdate());
        this.$addCtrl.on('click', () => this.onEdit());
    }

    showLines() {
        this.$formSection.hide();
        this.$lines.show();

        this.formTriggerElement?.focus();
        this.formTriggerElement = null;
    }

    showForm() {
        this.$lines.hide();
        this.$formSection.show();

        this.formTriggerElement = document.activeElement;
        this.form.focus();
    }
}

$(() => {
    const rootEls = document.querySelectorAll('.js-address-book');
    rootEls?.forEach((el) => {
        const $el = $(el);
        const renderingContext = parseRenderingContext(el);

        const addressBook = new AddressBook($el, renderingContext);
        
        if(addressBookStore.loaded) {
            addressBook.init();
        } else {
            const unwatch = addressBookStore.watch('loaded', (...[,,loaded]) =>  {
                if(loaded) {
                    addressBook.init();
                    unwatch();
                }
            });
        }
    });

    if(rootEls.length && !addressBookStore.loaded) {
        addressBookStore.actions.getAddresses();
    }
});
